import React, { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Dialog, DialogContent, LinearProgress } from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useLookups } from '../../../hooks/useLookups';
import { Passenger } from '../../../data/models/passenger.model';
import { ServicePlan } from '../../../data/models/service-plan.model';
import * as uiSlice from '../../../data/ui.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import * as reservationSlice from '../../../data/reservation.slice';
import * as clientSlice from '../../../data/client.slice';
import * as accountingSlice from '../../../data/accounting.slice';

import { useClientMemberContact } from '../../../hooks/useClientMemberContact';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import reservationUtils from '../../../services/reservationUtils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import {
  Reservation,
  ReservationValidations,
  StandingOrder,
  TripLegType,
} from '../../../data/models/reservation.model';
import ConfirmationDialog from '../../ConfirmationDialog';
import ReservationDialogHeader from './ReservationDialogHeader';
import ReservationTripLegForm from './ReservationTripLegForm';
import OverrideDialog from './OverrideDialog';
import { DateTime } from 'luxon/src/luxon';
import ReservationMemberForm from './ReservationMemberForm';
import ReservationSummary from './ReservationSummary';
import { useFeatureStandingOrderReturnSpecification } from '../../../hooks/features/useFeatureStandingOrderReturnSpecification';
import { useFeatureAddressValidationForCurrentPortal } from '../../../hooks/features/useFeatureAddressValidation';
import { usePortalLabel } from '../../../hooks/usePortalLabel';
import {
  runBundledValidation,
  clearTripLegScoreCache,
  scoreAndMergeConfidenceIntoTripLegs,
} from '../../reservations/machines/runBundledValidation';
import {
  getLegacyTripLegConfidenceFieldErrors,
  type TripLegConfidenceErrorPayload,
} from '../../reservations/machines/tripLegConfidence';

const PREFIX = 'ReservationDialog';

const classes = {
  content: `${PREFIX}-content`,
  pages: `${PREFIX}-pages`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    padding: 0,
    overflow: 'visible',
  },
  [`& .${classes.pages}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    maxWidth: '1152px',
    margin: '0 auto',
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(18),
      paddingRight: theme.spacing(18),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
  },
}));

interface ReservationDialogProps {
  title: string;
  open: boolean;
  passenger: Passenger;
  servicePlans: ServicePlan[];
  isPortal: boolean;
  close: () => void;
}

export default function ReservationDialog({
  title,
  open,
  passenger,
  servicePlans,
  isPortal,
  close,
}: ReservationDialogProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const enableReturnTripOverrides =
    useFeatureStandingOrderReturnSpecification();
  // {{clientName}}/{{clientNumber}} interpolation for the address-confidence
  // message. The contact is plan-scoped, so the passenger's service plan rides
  // along.
  const clientMemberContact = useClientMemberContact(
    passenger.clientBC,
    passenger.servicePlans?.[0]?.servicePlanUUID,
  );

  useEffect(() => {
    dispatch(lookupSlice.fetchTransportationAttributeMapAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegOverrideReason'));
    dispatch(clientSlice.fetchClientContactsAsync(passenger.clientBC));
  }, [dispatch]);

  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const passengerClientData = useAppSelector(
    clientSlice.selectPassengerClientData,
  );

  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );
  useLookups([
    'alternateContactType',
    'tripLegDriverNoteType',
    'tripLegPassengerNoteType',
  ]);
  const timeZones = useAppSelector(lookupSlice.selectTimeZones);
  const passengerNoteTypes = useAppSelector(
    lookupSlice.selectPassengerNoteTypes,
  );
  const driverNoteTypes = useAppSelector(lookupSlice.selectDriverNoteTypes);
  const alternateContactTypes = useAppSelector(
    lookupSlice.selectAlternateContactTypes,
  );
  const savingReservation = useAppSelector(
    reservationSlice.selectSavingReservation,
  );
  const savedReservation = useAppSelector(
    reservationSlice.selectSavedReservation,
  );

  const addressValidationEnabled =
    useFeatureAddressValidationForCurrentPortal();
  const isClientPortal = usePortalLabel() === 'client-portal';
  const [validations, setValidations] =
    React.useState<ReservationValidations | null>(null);
  // Blocking trip-leg address-confidence errors (/score/trip-leg) keyed by leg
  // index. Kept separate from `validations` because runBundledValidation returns
  // them alongside — and sometimes instead of — the /validate result.
  const [tripLegErrors, setTripLegErrors] = React.useState<
    Record<number, TripLegConfidenceErrorPayload[]>
  >({});

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: reservationUtils.getDefaults(
      passenger,
      null,
      transportationAttributes,
    ),
  });

  const formHookFields = useFieldArray({
    control: formHook.control,
    name: 'MultiLegTrips' as any,
  });
  const steps = [
    t('reservation.tripDetailsStep'),
    t('reservation.member'),
    t('reservation.summary'),
  ];
  const {
    watch,
    getValues,
    formState: { isDirty, isValid, errors },
  } = formHook;

  const [activeStep, setActiveStep] = React.useState(0);
  const [maxStep, setMaxStep] = React.useState(0);
  const [overrideDialogOpen, setOverrideDialogOpen] = React.useState(false);
  const [isOverridden, setIsOverridden] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [validationMessages, setValidationMessages] = React.useState<
    {
      field: string;
      message: string;
    }[]
  >([]);
  const [isDialogInitialized, setIsDialogInitialized] = React.useState(false);
  const pickUpTime = watch('MultiLegTrips[0].PickupTime');
  const isWillCall = watch('MultiLegTrips[0].WillCall');
  const appointmentDate = watch('MultiLegTrips[0].AppointmentTime');

  const tripLegServiceDate = (pickUpTime || appointmentDate)?.toISODate();
  const overridableServicePlans = servicePlans.filter(
    ({ effectiveStartDate, effectiveEndDate }) =>
      effectiveStartDate <= tripLegServiceDate &&
      (!effectiveEndDate || tripLegServiceDate <= effectiveEndDate),
  );
  const formValues = watch();
  const { MultiLegTrips } = formValues;
  const [reservation, setReservation] = React.useState<Reservation>();
  const [standingOrder, setStandingOrder] = React.useState<StandingOrder>();

  const clearCustomErrors = () => {
    const fields = validationMessages.map((msg) => msg.field);
    formHook.clearErrors(fields);
  };

  const tripType = watch('TripType');

  const handleConfirmationDialogOpen = () => {
    setConfirmOpen(true);
  };

  const handleConfirmationDialogClose = () => {
    setConfirmOpen(false);
  };

  React.useEffect(() => {
    if (open) {
      setValidationMessages([]);
      setValidations(null);
      setTripLegErrors({});
      setIsOverridden(false);
      setIsDialogInitialized(false);
      formHook.clearErrors();
      clearTripLegScoreCache();
      dispatch(reservationSlice.reset());
    }
  }, [open]);

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let abortController: AbortController | undefined;
    const subscription = watch((value, { name }) => {
      if (open) {
        const formData = getValues();
        const firstLeg = formData.MultiLegTrips?.[0];
        const hasAppointmentTime = !!firstLeg?.AppointmentTime;
        const hasPickupTime = !!firstLeg?.PickupTime;

        if (!hasAppointmentTime && !hasPickupTime) {
          return;
        }

        if (!isDialogInitialized) {
          setIsDialogInitialized(true);
        }

        const reservation = reservationUtils.getPartialReservationFromData(
          passenger.clientBC,
          passenger.passengerUUID!,
          formData,
        );

        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
          // Abort any in-flight run so a slower earlier request can't clobber
          // the latest edit's result.
          abortController?.abort();
          const controller = new AbortController();
          abortController = controller;
          // Mirror the old validateReservationAsync / TripLegForm-self-score
          // timing so the child ReservationTripLegForm keeps disabling Next
          // while a run is in flight.
          dispatch(reservationSlice.setLoadingReservationValidations(true));
          dispatch(reservationSlice.setLoadingReservationConfidence(true));
          runBundledValidation(
            {
              reservation,
              addressValidationEnabled,
            },
            controller.signal,
          )
            .then((output) => {
              if (controller.signal.aborted) return;
              setValidations(output.validations);
              setTripLegErrors(output.errorsByTripIndex);
              // Bridge the bundled result into redux so the child forms behave
              // exactly as under the old validateReservationAsync +
              // TripLegForm-self-score flow: ReservationTripLegForm's Next gate
              // reads the loading flags + reservationConfidence, and TripLegForm
              // renders backend timezones + per-portal low-confidence warnings.
              dispatch(
                reservationSlice.setReservationValidations({
                  ...output.validations,
                  reservationReferenceID: reservation.reservationReferenceID,
                }),
              );
              dispatch(reservationSlice.setLoadedReservationValidations(true));
              dispatch(
                reservationSlice.setLoadingReservationValidations(false),
              );
              // Only bridge confidence for two-sided, form-visible legs. A round
              // trip's hidden return leg (index >= form leg count) and any
              // one-sided leg carry no `tripLegConfidence`, and
              // ReservationTripLegForm reads `rc.tripLegConfidence` unguarded —
              // so dispatching them would crash its Next gate.
              const formLegCount = formData.MultiLegTrips?.length ?? 0;
              let scoredAnyLeg = false;
              Object.entries(output.confidenceByTripIndex).forEach(
                ([idx, entry]) => {
                  if (
                    Number(idx) < formLegCount &&
                    entry.hasPickup &&
                    entry.hasDropoff
                  ) {
                    dispatch(
                      reservationSlice.setReservationConfidence({
                        index: Number(idx),
                        confidence: entry.response,
                      }),
                    );
                    scoredAnyLeg = true;
                  }
                },
              );
              if (scoredAnyLeg) {
                dispatch(reservationSlice.setLoadedReservationConfidence(true));
              }
              dispatch(reservationSlice.setLoadingReservationConfidence(false));
            })
            .catch(() => {
              if (controller.signal.aborted) return;
              // runBundledValidation swallows /validate failures; on a hard
              // rejection just clear the in-flight flags so Next is not stuck.
              dispatch(
                reservationSlice.setLoadingReservationValidations(false),
              );
              dispatch(reservationSlice.setLoadingReservationConfidence(false));
            });
        }, 500);
      }
    });
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      abortController?.abort();
      subscription.unsubscribe();
    };
  }, [open, isDialogInitialized, addressValidationEnabled]);

  React.useEffect(() => {
    if (!isDialogInitialized) {
      if (validationMessages.length) {
        setValidationMessages([]);
      }
      return;
    }

    const formData = getValues();
    const firstLeg = formData.MultiLegTrips?.[0];
    const hasAppointmentTime = !!firstLeg?.AppointmentTime;
    const hasPickupTime = !!firstLeg?.PickupTime;

    if (!hasAppointmentTime && !hasPickupTime) {
      if (validationMessages.length) {
        setValidationMessages([]);
      }
      return;
    }

    // Trip-leg address-confidence errors already carry a resolved form field
    // path (round-trip flip included); append last so a trip-leg error wins over
    // a /validate error targeting the same field.
    const tripLegMessages = getLegacyTripLegConfidenceFieldErrors(
      t,
      tripLegErrors,
      clientMemberContact,
      isClientPortal,
    );

    if (
      !isOverridden &&
      (validations?.errors?.length || tripLegMessages.length)
    ) {
      const validateMessages = validations?.errors?.length
        ? reservationUtils.getFieldErrors(
            t,
            tripType === TripLegType.RoundTrip,
            validations,
            clientMemberContact,
          )
        : [];
      setValidationMessages([...validateMessages, ...tripLegMessages]);
      return;
    }

    if (validationMessages.length) {
      setValidationMessages([]);
      formHook.trigger(undefined, { shouldFocus: false });
    }
  }, [
    validations,
    tripLegErrors,
    clientMemberContact,
    isClientPortal,
    formHook,
    activeStep,
    isValid,
    validationMessages.length,
    isDialogInitialized,
  ]);

  const prevValidationFieldsRef = React.useRef<string[]>([]);
  React.useEffect(() => {
    const newFields = validationMessages.map((msg) => msg.field);
    const removedFields = prevValidationFieldsRef.current.filter(
      (f) => !newFields.includes(f),
    );
    if (removedFields.length) {
      formHook.clearErrors(removedFields);
    }
    validationMessages.forEach((msg) => {
      formHook.setError(msg.field, {
        type: 'custom',
        message: msg.message,
      });
    });
    prevValidationFieldsRef.current = newFields;
  }, [validationMessages]);

  React.useEffect(() => {
    if (saveSuccessMessage) {
      handleClose();
    }
  }, [saveSuccessMessage]);

  const gotoStep = (step: number) => {
    const res = reservationUtils.getReservationFromFormData(
      passenger.clientBC,
      passenger.passengerUUID!,
      formValues,
      [],
      isGurney || isBariatricGurney
        ? formValues.MultiLegTrips.map((leg: any) => ({
            gurneyTypeTransportationAttributeUUID: leg.AttributeLevel2,
            memberHeightInches: parseInt(leg.height),
            memberWeightLbs: parseInt(leg.weight),
            oxygenCapacityTransportationAttributeUUID:
              leg.AttributeLevel4 || null,
            stairsCountTransportationAttributeUUID: leg.AttributeLevel5 || null,
            waitAndReturn: false,
          }))?.[0]
        : undefined,
    );
    const so = reservationUtils.getStandingOrder(
      formValues,
      enableReturnTripOverrides,
    );
    setReservation(res);
    setStandingOrder(so);
    setActiveStep(step);
    setMaxStep((prev) => Math.max(prev, step));
    formHook.reset(formValues, {
      keepValues: true,
      keepDirty: true,
      keepDirtyValues: true,
      keepTouched: true,
    });
  };

  const handleStep = (step: number) => () => {
    if (!isPortal || step <= maxStep) {
      gotoStep(step);
    }
  };

  const handlePrevious = () => {
    gotoStep(activeStep - 1);
  };

  const handleNext = () => {
    gotoStep(activeStep + 1);
  };

  const handleSubmit = () => {
    if (!savingReservation && !savedReservation) {
      formHook.handleSubmit(done)();
    }
  };

  const goBack = (evt: any) => {
    evt.preventDefault();
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    formHook.trigger();
  };

  const saveAsDraft = async () => {
    const reservation = reservationUtils.getReservationFromFormData(
      passenger.clientBC,
      passenger.passengerUUID!,
      formValues,
      [],
      isGurney || isBariatricGurney
        ? formValues.MultiLegTrips.map((leg: any) => ({
            gurneyTypeTransportationAttributeUUID: leg.AttributeLevel2,
            memberHeightInches: parseInt(leg.height),
            memberWeightLbs: parseInt(leg.weight),
            oxygenCapacityTransportationAttributeUUID:
              leg.AttributeLevel4 || undefined,
            stairsCountTransportationAttributeUUID:
              leg.AttributeLevel5 || undefined,
            waitAndReturn: false,
          }))?.[0]
        : undefined,
    );
    // Mark saving before the pre-dispatch scoring so the buttons (gated on
    // savingReservation) can't fire a second submit during the await.
    dispatch(reservationSlice.setSavingReservation(true));
    const reservationToSend = addressValidationEnabled
      ? {
          ...reservation,
          tripLegs: await scoreAndMergeConfidenceIntoTripLegs(
            reservation.tripLegs,
            false,
          ),
        }
      : reservation;
    dispatch(
      reservationSlice.createReservationAsync(
        reservationToSend,
        standingOrder,
        true,
      ),
    );
  };

  const handleClose = () => {
    dispatch(reservationSlice.setSavedReservation(false));
    dispatch(reservationSlice.setSavingReservation(false));
    setActiveStep(0);
    setMaxStep(0);
    setIsOverridden(false);
    setIsDialogInitialized(false);
    setValidationMessages([]);
    setValidations(null);
    setTripLegErrors({});
    formHook.reset();
    formHook.clearErrors();
    setConfirmOpen(false);
    clearTripLegScoreCache();

    dispatch(reservationSlice.reset());
    close();
  };

  const done = async (data: any) => {
    const reservation = reservationUtils.getReservationFromFormData(
      passenger.clientBC,
      passenger.passengerUUID!,
      data,
      [],
      reservationUtils.getPassengerPhone(passenger),
      isGurney || isBariatricGurney
        ? data.MultiLegTrips.map((leg: any) => ({
            gurneyTypeTransportationAttributeUUID: leg.AttributeLevel2,
            memberHeightInches: parseInt(leg.height),
            memberWeightLbs: parseInt(leg.weight),
            oxygenCapacityTransportationAttributeUUID:
              leg.AttributeLevel4 || undefined,
            stairsCountTransportationAttributeUUID:
              leg.AttributeLevel5 || undefined,
            waitAndReturn: false,
          }))?.[0]
        : undefined,
    );
    // Mark saving before the pre-dispatch scoring so handleSubmit's
    // !savingReservation guard can't fire a second submit during the await.
    dispatch(reservationSlice.setSavingReservation(true));
    const reservationToSend = addressValidationEnabled
      ? {
          ...reservation,
          tripLegs: await scoreAndMergeConfidenceIntoTripLegs(
            reservation.tripLegs,
            false,
          ),
        }
      : reservation;
    dispatch(
      reservationSlice.createReservationAsync(
        reservationToSend,
        standingOrder,
        false,
      ),
    );
  };

  const addLeg = () => {
    formHookFields.append(reservationUtils.getLegDefaults(passenger));
  };

  const handleOverride = () => {
    setOverrideDialogOpen(true);
  };

  const handleOverrideClose = (data: any | null) => {
    if (data) {
      setIsOverridden(true);
      formHook.setValue('AllowOverride', data.AllowOverride);
      formHook.setValue('OverrideFirstName', data.OverrideFirstName);
      formHook.setValue('OverrideLastName', data.OverrideLastName);
      formHook.setValue('OverrideReason', data.OverrideReason);
      formHook.setValue('OverrideServicePlan', data.ServicePlan);
      clearCustomErrors();
    }
    setOverrideDialogOpen(false);
    formHook.trigger();
  };
  const isFormDirty = () => {
    const dirtyFieldExcluded = 'TripType';
    const fieldToExclude = Object.keys(formHook.formState.dirtyFields).filter(
      (field) => field !== dirtyFieldExcluded,
    );
    if ((isDirty && fieldToExclude.length > 0) || activeStep > 0) {
      handleConfirmationDialogOpen();
    } else {
      handleClose();
    }
  };

  const isFormValid = useMemo(() => {
    let basicValid = isValid && Object.keys(errors).length === 0;

    // Only the first page of the wizard has specific checks for fields to be made.
    // The other pages the standard react-hook-form validation works.
    if (activeStep !== 0) {
      return basicValid;
    }

    // From here on we look for specific errors because the isValid is not always accurate.
    // TODO: revisit this when we update react-hook-form and the MUI library because it
    //       looks like a lot of work was done in this area of those libraries.

    // First check for specific things in the reservation that are required
    MultiLegTrips?.forEach((leg: any) => {
      if (leg.AdditionalPassengers && leg.AdditionalPassengerCount < 1) {
        basicValid = false;
      }

      if (!leg.PickupAddress || !leg.DropoffAddress) {
        basicValid = false;
      }

      if (!leg.AppointmentTime) {
        basicValid = false;
      }

      if (!leg.AttributeLevel1) {
        basicValid = false;
      }
      if (!leg.MobilityAttribute) {
        basicValid = false;
      }
    });

    // If we have all the basics, check that standing reservations are set up
    // correctly if that option is chosen.
    const isValidStandingOrderDay = (
      checked: boolean,
      appt: DateTime | null,
      pickup: DateTime | null,
    ): boolean => {
      return !checked || !!(appt?.isValid && pickup?.isValid);
    };
    if (basicValid) {
      if (formValues.StandingOrder) {
        const hasAtLeastOneDay =
          formValues.StandingOrderMonday ||
          formValues.StandingOrderTuesday ||
          formValues.StandingOrderWednesday ||
          formValues.StandingOrderThursday ||
          formValues.StandingOrderFriday ||
          formValues.StandingOrderSaturday ||
          formValues.StandingOrderSunday;
        if (
          !formValues.StandingOrderStartDate?.isValid ||
          !formValues.StandingOrderEndDate?.isValid ||
          !hasAtLeastOneDay
        ) {
          basicValid = false;
        } else {
          basicValid =
            basicValid &&
            isValidStandingOrderDay(
              formValues.StandingOrderMonday,
              formValues.StandingOrderMondayAppointmentTime,
              formValues.StandingOrderMondayPickupTime,
            );
          basicValid =
            basicValid &&
            isValidStandingOrderDay(
              formValues.StandingOrderTuesday,
              formValues.StandingOrderTuesdayAppointmentTime,
              formValues.StandingOrderTuesdayPickupTime,
            );
          basicValid =
            basicValid &&
            isValidStandingOrderDay(
              formValues.StandingOrderWednesday,
              formValues.StandingOrderWednesdayAppointmentTime,
              formValues.StandingOrderWednesdayPickupTime,
            );
          basicValid =
            basicValid &&
            isValidStandingOrderDay(
              formValues.StandingOrderThursday,
              formValues.StandingOrderThursdayAppointmentTime,
              formValues.StandingOrderThursdayPickupTime,
            );
          basicValid =
            basicValid &&
            isValidStandingOrderDay(
              formValues.StandingOrderFriday,
              formValues.StandingOrderFridayAppointmentTime,
              formValues.StandingOrderFridayPickupTime,
            );
          basicValid =
            basicValid &&
            isValidStandingOrderDay(
              formValues.StandingOrderSaturday,
              formValues.StandingOrderSaturdayAppointmentTime,
              formValues.StandingOrderSaturdayPickupTime,
            );
          basicValid =
            basicValid &&
            isValidStandingOrderDay(
              formValues.StandingOrderSunday,
              formValues.StandingOrderSundayAppointmentTime,
              formValues.StandingOrderSundayPickupTime,
            );
        }
      }
    }

    return basicValid;
  }, [activeStep, isValid, formValues, MultiLegTrips]);

  const canSaveAsDraft = useMemo(() => {
    let canSave: boolean = true;

    formValues.MultiLegTrips?.forEach((leg: any) => {
      canSave = canSave && !!leg.TripPurpose;
      canSave = canSave && !!leg.AppointmentTime;
      canSave = canSave && (!!leg.PickupTime || isWillCall);
      canSave = canSave && !!leg.PickupAddress;
      canSave = canSave && !!leg.DropoffAddress;
      canSave = canSave && !!leg.AttributeLevel1;
      canSave = canSave && !!leg.MobilityAttribute;
    });

    return canSave;
  }, [formValues]);

  const reverseTransportationAttributeMap = useAppSelector(
    lookupSlice.selectReverseTransportationAttributeMap,
  );
  const secondLevelAttribute = watch('MultiLegTrips[0].AttributeLevel2');
  const gurneyAttribute = useMemo(
    () => reverseTransportationAttributeMap['GURNEY'],
    [reverseTransportationAttributeMap],
  );
  const bariatricGurneyAttribute = useMemo(
    () => reverseTransportationAttributeMap['BARIATRIC_GURNEY'],
    [reverseTransportationAttributeMap],
  );
  const isGurney = useMemo(() => {
    return secondLevelAttribute === gurneyAttribute;
  }, [secondLevelAttribute, gurneyAttribute]);
  const isBariatricGurney = useMemo(() => {
    return secondLevelAttribute === bariatricGurneyAttribute;
  }, [secondLevelAttribute, bariatricGurneyAttribute]);

  const formAllowOverride = formHook.watch('AllowOverride');
  const allowOverride = useMemo(() => {
    // yes, this is a mess.  We're awaiting docs.
    if (formAllowOverride) {
      return false;
    }

    // GenericErrors are from the API but don't map to a field.
    const hasGenericError = validationMessages?.some(
      (e) => e.field === 'GenericError',
    );

    const hasFieldErrors = validationMessages?.some(
      (e) => e.field !== 'GenericError',
    );

    if ((isGurney || isBariatricGurney) && !isOverridden) {
      return true;
    }

    if (hasFieldErrors || hasGenericError) {
      return true;
    }

    if ((hasFieldErrors || hasGenericError) && !Boolean(formAllowOverride)) {
      return true;
    }
    return false;
  }, [validationMessages, isValid, formAllowOverride]);

  return (
    <RootDialog
      fullScreen
      open={open}
      onClose={handleClose}
      aria-labelledby="reservation-dialog-title">
      <ReservationDialogHeader
        passengerName={
          isPortal
            ? undefined
            : `${passenger?.firstName} ${passenger?.lastName}`
        }
        title={title}
        steps={steps}
        activeStep={activeStep}
        isValid={isFormValid}
        step={handleStep}
        open={isFormDirty}
      />
      {savingReservation && <LinearProgress />}
      <DialogContent className={classes.content}>
        <Box className={classes.pages}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            {activeStep === 0 && (
              <ReservationTripLegForm
                key={activeStep - 1}
                clientBC={passenger.clientBC}
                tripType={tripType}
                tripPurposes={passengerClientData.purposeCodes}
                timeZones={timeZones}
                servicePlans={servicePlans}
                transportationAttributes={transportationAttributes}
                formHook={formHook}
                formHookFields={formHookFields}
                isSaving={savingReservation}
                isValid={isFormValid}
                allowOverride={allowOverride}
                canSaveAsDraft={canSaveAsDraft}
                isPortalReservation={isPortal}
                addLeg={addLeg}
                next={handleNext}
                saveAsDraft={saveAsDraft}
                passengerAddresses={passenger.passengerAddresses}
                override={handleOverride}
              />
            )}
            {activeStep === 1 && (
              <ReservationMemberForm
                stepCount={steps.length - 1}
                tripCount={formHookFields.fields.length}
                tripType={tripType}
                transportationAttributes={transportationAttributes}
                passengerNoteTypes={passengerNoteTypes}
                driverNoteTypes={driverNoteTypes}
                alternateContactTypes={alternateContactTypes}
                servicePlans={servicePlans}
                formHook={formHook}
                isSaving={savingReservation}
                isValid={isFormValid}
                allowOverride={allowOverride}
                isOverridden={isOverridden}
                canSaveAsDraft={canSaveAsDraft}
                isPortalReservation={isPortal}
                goBack={goBack}
                next={handleNext}
                override={handleOverride}
                saveAsDraft={saveAsDraft}
              />
            )}
            {activeStep === 2 && reservation && (
              <ReservationSummary
                passenger={passenger}
                reservation={reservation}
                standingOrder={standingOrder}
                tripCount={formHookFields.fields.length}
                formHook={formHook}
                servicePlans={servicePlans}
                isSaving={savingReservation}
                canSaveAsDraft={canSaveAsDraft}
                goBack={goBack}
                done={handleSubmit}
                saveAsDraft={saveAsDraft}
                isPortalReservation={isPortal}
              />
            )}
          </LocalizationProvider>
        </Box>
      </DialogContent>
      {savingReservation && <LinearProgress />}
      <OverrideDialog
        open={overrideDialogOpen}
        servicePlans={overridableServicePlans}
        close={handleOverrideClose}
      />{' '}
      <ConfirmationDialog
        title={t('common.discardChanges')}
        message={t('common.possibleDataLoss')}
        open={confirmOpen}
        positiveAction={handleClose}
        negativeAction={handleConfirmationDialogClose}
      />
    </RootDialog>
  );
}
